import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import dayjs from "dayjs";
import {
  encerramentoApi,
  type PedidoAguardando,
  type PreviaDoEncerramento,
} from "../../../profiles-tracking/services/encerramento";

// Pedido do Reinaldo em 29/10/2026: encerrar impulsionamento passa pela aprovação do
// Thiago, do Igor, da Ana Paula ou da Roberta. A lista só aparece para eles (o backend devolve vazio
// para os outros); aprovar exige abrir antes o que será apagado.

type Aberto = { previa: PreviaDoEncerramento | null; carregando: boolean };

const semChave = (abertos: Record<string, Aberto>, id: string) => {
  const resto = { ...abertos };
  delete resto[id];
  return resto;
};

export const EncerramentosAguardando: React.FC = () => {
  const [pedidos, setPedidos] = useState<PedidoAguardando[]>([]);
  const [abertos, setAbertos] = useState<Record<string, Aberto>>({});
  const [enviando, setEnviando] = useState<string | null>(null);
  const [mensagem, setMensagem] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      setPedidos(await encerramentoApi.aguardando());
    } catch {
      setPedidos([]);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const abrir = async (pedido: PedidoAguardando) => {
    const id = pedido.profilePublicId;
    if (abertos[id]) {
      setAbertos((atual) => semChave(atual, id));
      return;
    }
    setAbertos((atual) => ({ ...atual, [id]: { previa: null, carregando: true } }));
    try {
      const previa = await encerramentoApi.previa(id);
      setAbertos((atual) => ({ ...atual, [id]: { previa, carregando: false } }));
    } catch (e) {
      setAbertos((atual) => semChave(atual, id));
      setErro(e instanceof Error ? e.message : "Não foi possível carregar a prévia.");
    }
  };

  const decidir = async (pedido: PedidoAguardando, acao: "aprovar" | "recusar") => {
    setEnviando(pedido.profilePublicId);
    setErro(null);
    setMensagem(null);
    try {
      if (acao === "aprovar") {
        await encerramentoApi.aprovar(pedido.profilePublicId);
        setMensagem(`Aprovado: o encerramento de ${pedido.perfilNome} começou.`);
      } else {
        await encerramentoApi.recusar(pedido.profilePublicId);
        setMensagem(`Recusado: ${pedido.perfilNome} segue com as automações.`);
      }
      await carregar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível registrar a decisão.");
      await carregar();
    } finally {
      setEnviando(null);
    }
  };

  if (pedidos.length === 0 && !mensagem && !erro) return null;

  return (
    <Cartao aria-label="Encerramentos aguardando aprovação">
      <h3>
        <span className="material-icons" aria-hidden="true">pending_actions</span>
        Encerramentos aguardando aprovação{pedidos.length > 0 ? ` (${pedidos.length})` : ""}
      </h3>
      {mensagem && <Ok role="status">{mensagem}</Ok>}
      {erro && <Erro role="alert">{erro}</Erro>}
      <Lista>
        {pedidos.map((pedido) => {
          const aberto = abertos[pedido.profilePublicId];
          const ocupado = enviando === pedido.profilePublicId;
          return (
            <li key={pedido.id}>
              <Linha>
                <div>
                  <strong>{pedido.perfilNome}</strong>
                  <small>
                    Pedido por {pedido.solicitadoPor || "—"}
                    {pedido.solicitadoEm ? ` em ${dayjs(pedido.solicitadoEm).format("DD/MM/YYYY HH:mm")}` : ""}
                    {pedido.removerAcesso ? " · tira o nosso acesso" : " · mantém o nosso acesso"}
                  </small>
                </div>
                <Botoes>
                  <BotaoLink type="button" onClick={() => void abrir(pedido)} aria-expanded={!!aberto}>
                    {aberto ? "Fechar" : "Ver o que será apagado"}
                  </BotaoLink>
                  <BotaoNeutro type="button" disabled={ocupado} onClick={() => void decidir(pedido, "recusar")}>
                    Recusar
                  </BotaoNeutro>
                  <BotaoPerigo
                    type="button"
                    disabled={ocupado || !aberto?.previa}
                    title={aberto?.previa ? undefined : "Abra o que será apagado antes de aprovar"}
                    onClick={() => void decidir(pedido, "aprovar")}
                  >
                    {ocupado ? "Enviando…" : "Aprovar"}
                  </BotaoPerigo>
                </Botoes>
              </Linha>
              {aberto?.carregando && <small>Carregando…</small>}
              {aberto?.previa && (
                <Resumo>
                  {aberto.previa.resumo.map((linha) => (
                    <li key={linha}>{linha}</li>
                  ))}
                </Resumo>
              )}
            </li>
          );
        })}
      </Lista>
    </Cartao>
  );
};

const Cartao = styled.section`
  width: calc(100% - 64px);
  margin-top: 24px;
  box-sizing: border-box;
  padding: 16px 20px;
  border-radius: 16px;
  border: 1px solid #f4c7c3;
  background: #fffaf9;
  font-family: var(--font-sans);
  color: var(--text, #3c4043);

  h3 {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 0 8px;
    font-size: 15px;
    color: #b3261e;
  }

  @media (max-width: 768px) {
    width: calc(100% - 32px);
    padding: 12px 14px;
  }
`;

const Lista = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;

  li + li {
    border-top: 1px solid #f1e3e1;
    padding-top: 10px;
  }
`;

const Linha = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  > div:first-child {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    font-size: 14px;
  }

  small {
    color: #5f6368;
    font-size: 12px;
  }
`;

const Botoes = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const Resumo = styled.ul`
  margin: 8px 0 0;
  padding-left: 20px;
  font-size: 13px;
  line-height: 1.6;
`;

const BotaoLink = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  color: var(--brand-700, #1a73e8);
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  text-decoration: underline;
`;

const BotaoNeutro = styled.button`
  height: 34px;
  padding: 0 14px;
  border: 1px solid #dadce0;
  background: #ffffff;
  border-radius: 17px;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
`;

const BotaoPerigo = styled.button`
  height: 34px;
  padding: 0 14px;
  border: none;
  background: #b3261e;
  color: #ffffff;
  border-radius: 17px;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;

  &:disabled {
    background: #f1b5b0;
    cursor: not-allowed;
  }
`;

const Ok = styled.p`
  margin: 4px 0 8px;
  color: #1e8e3e;
  font-size: 13px;
`;

const Erro = styled.p`
  margin: 4px 0 8px;
  color: #b3261e;
  font-size: 13px;
`;
